'use client';

import { useState } from 'react';
import { Plus } from 'lucide-react';
import type { PageBlock, PageBlockType } from '@impact/types';
import { Button } from '@/components/ui/button/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { BlockPicker } from '@/components/page-editor/v2/pickers/BlockPicker';
import { blockRegistry } from '@/components/page-editor/v2/registry';
import { usePageEditorStore } from '@/components/page-editor/v2/store/usePageEditor';
import { setPath } from '@/components/page-editor/v2/lib/setPath';
import { readPath } from '@/components/page-editor/v2/lib/readPath';
import { cn } from '@/lib/utils';

interface Props {
  blockId: string;
  arrayPath: string;
  variant?: 'inline' | 'placeholder';
  label?: string;
}

export function AddNestedBlockButton({
  blockId,
  arrayPath,
  variant = 'inline',
  label = 'Add block',
}: Props) {
  const [open, setOpen] = useState(false);
  const block = usePageEditorStore((s) => s.blocks.find((b) => b.id === blockId));
  const updateBlock = usePageEditorStore((s) => s.updateBlock);

  const handleSelect = (type: PageBlockType) => {
    if (!block) return;
    const cfg = blockRegistry[type];
    const newBlock = {
      id: crypto.randomUUID(),
      type,
      data: cfg.createDefault(),
    } as PageBlock;
    const current = (readPath(block, arrayPath) as PageBlock[]) ?? [];
    const next = setPath(block, arrayPath, [...current, newBlock]);
    updateBlock(blockId, next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={variant === 'placeholder' ? 'outline' : 'ghost'}
          className={cn(
            variant === 'placeholder'
              ? 'mx-auto flex h-auto flex-col items-center gap-1 border-2 border-dashed border-muted-foreground/30 bg-muted/20 px-6 py-8 text-muted-foreground hover:border-primary hover:bg-muted/40 hover:text-primary'
              : 'h-7 px-2 text-xs text-muted-foreground hover:text-foreground',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Plus className={variant === 'placeholder' ? 'h-5 w-5' : 'h-3.5 w-3.5'} />
          <span>{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" className="p-0" onClick={(e) => e.stopPropagation()}>
        <BlockPicker onSelect={handleSelect} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
